import { inject } from "@angular/core";
import { Route, Router } from "@angular/router";
import { initialDataResolver } from "app/app.resolvers";
import { AuthGuard } from "app/core/auth/guards/auth.guard";
import { NoAuthGuard } from "app/core/auth/guards/noAuth.guard";
import { LayoutComponent } from "app/layout/layout.component";
import { OverviewComponent } from "app/modules/overview/overview.component";
import { VenuesComponent } from "app/modules/venues/venues.component";
import { CourtsComponent } from "app/modules/courts/courts.component";
import { AuditReportComponent } from "./modules/report/audit-report/audit-report.component";
import { PolicyComponent } from "./modules/policy/policy.component";
import { UsersDetailsComponent } from "./modules/users-details/users-details.component";
import { UserFeedbackComponent } from "./modules/user-feedback/user-feedback.component";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect signed-in user to the '/example'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: "", pathMatch: "full", redirectTo: "company" },
  { path: "signed-in-redirect", pathMatch: "full", redirectTo: "company" },

  // Auth routes for guests
  {
    path: "",
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "confirmation-required",
        loadChildren: () =>
          import(
            "app/modules/auth/confirmation-required/confirmation-required.routes"
          ),
      },
      {
        path: "forgot-password",
        loadChildren: () =>
          import("app/modules/auth/forgot-password/forgot-password.routes"),
      },
      {
        path: "reset-password",
        loadChildren: () =>
          import("app/modules/auth/reset-password/reset-password.routes"),
      },
      {
        path: "sign-in",
        loadChildren: () => import("app/modules/auth/sign-in/sign-in.routes"),
      },
      {
        path: "accept-invitation",
        loadChildren: () =>
          import(
            "app/modules/auth/accept-platform-invitation/accept-platform-invitation.routes"
          ),
      },
      {
        path: "oauth-callback",
        loadChildren: () =>
          import("app/modules/auth/oauth-callback/oauth-callback.routes"),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "sign-out",
        loadChildren: () => import("app/modules/auth/sign-out/sign-out.routes"),
      },
      {
        path: "unlock-session",
        loadChildren: () =>
          import("app/modules/auth/unlock-session/unlock-session.routes"),
      },
    ],
  },

  // Landing routes
  {
    path: "",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "home",
        loadChildren: () => import("app/modules/landing/home/home.routes"),
      },
      {
        path: "dashboard",
        loadChildren: () => import("app/modules/landing/home/home.routes"),
      },
      { path: "policy", component: PolicyComponent },
    ],
  },

  // Admin routes
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: "classic",
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      { path: "company", component: OverviewComponent },
      { path: "payment-splits", pathMatch: "full", redirectTo: "company" },
      {
        path: "company/:companyId/payment-splits",
        pathMatch: "full",
        redirectTo: "company/:companyId/financial-settings",
      },
      {
        path: "company/:companyId/financial-settings",
        loadComponent: () =>
          import("app/modules/payment-splits/payment-splits.component").then(
            (m) => m.PaymentSplitsComponent,
          ),
      },
      { path: "venues", component: VenuesComponent },
      { path: "venue/:id", component: CourtsComponent },
      { path: "courts", component: CourtsComponent },
      {
        path: "settings",
        loadChildren: () =>
          import("app/modules/admin/settings/settings.routes"),
      },
      { path: "bookings", component: AuditReportComponent },
      {
        path: "notifications/audit",
        pathMatch: "full",
        redirectTo: () =>
          inject(Router).createUrlTree(["/settings"], {
            queryParams: { panel: "notification-audit" },
          }),
      },
      {
        path: "users",
        component: UsersDetailsComponent,
        data: { role: "all" },
      },
      {
        path: "users/players",
        component: UsersDetailsComponent,
        data: { role: "player" },
      },
      {
        path: "users/admins",
        component: UsersDetailsComponent,
        data: { role: "admin" },
      },

      { path: "feedback", component: UserFeedbackComponent },
    ],
  },
];
